import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, FileText, Zap, Star, Sparkles } from 'lucide-react';
import { CTAButton } from './Shared';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/nsoCwUc1TuzPSLQFLiDmAv/MOCKUP_LANDING.png',
  alt: 'Kit Reflexo-Pro - Tomo principal + planillas clínicas'
};
export const Hero = () => {
  return <div className="relative overflow-hidden pt-2 pb-8 md:pt-6 md:pb-12 bg-transparent" data-id="element-125">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-pink/20 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-126"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-127">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-128">
          <div className="inline-flex items-center justify-center bg-white border border-brand-pink/50 px-4 py-1.5 rounded-full mb-6 gap-2 shadow-sm pt-[2px] pb-[2px] pl-[10px] pr-[10px]" data-id="element-129">
            <span className="text-sm" data-id="element-130">🦶</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-text" data-id="element-131">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="relative inline-block mx-auto max-w-4xl" data-id="element-132">
            {/* Decorative sparkles around the title */}
            <Sparkles className="sparkle absolute -top-3 -left-2 md:-top-5 md:-left-6 w-3 h-3 md:w-5 md:h-5 text-[#E8A0B5] pointer-events-none" style={{
            animationDelay: '0s'
          }} aria-hidden="true" data-id="element-133" />
            <Sparkles className="sparkle absolute top-1/2 -left-4 md:-left-10 w-2.5 h-2.5 md:w-4 md:h-4 text-[#F4C2D2] pointer-events-none" style={{
            animationDelay: '1.1s'
          }} aria-hidden="true" data-id="element-134" />
            <Sparkles className="sparkle absolute -bottom-2 left-1/4 w-2 h-2 md:w-3.5 md:h-3.5 text-[#E8A0B5] pointer-events-none" style={{
            animationDelay: '1.8s'
          }} aria-hidden="true" data-id="element-135" />
            <Sparkles className="sparkle absolute -top-4 right-1/4 w-2.5 h-2.5 md:w-4 md:h-4 text-[#F4C2D2] pointer-events-none" style={{
            animationDelay: '0.6s'
          }} aria-hidden="true" data-id="element-136" />
            <Sparkles className="sparkle absolute top-1/3 -right-3 md:-right-8 w-3 h-3 md:w-5 md:h-5 text-[#D4738B] pointer-events-none" style={{
            animationDelay: '1.4s'
          }} aria-hidden="true" data-id="element-137" />
            <Sparkles className="sparkle absolute -bottom-3 right-6 md:right-10 w-2 h-2 md:w-3.5 md:h-3.5 text-[#E8A0B5] pointer-events-none" style={{
            animationDelay: '2.2s'
          }} aria-hidden="true" data-id="element-138" />

            <h1 className="title-shimmer sm:text-4xl md:text-6xl lg:text-7xl max-w-4xl mx-auto font-[800] font-[Cinzel,_serif] relative text-[28px] mb-1 leading-tight" data-id="element-139">
              Sesiones más efectivas
            </h1>

            <div className="flex items-center justify-center gap-2 my-2 md:my-3" data-id="element-140">
              <span className="h-px w-8 md:w-14 bg-gradient-to-r from-transparent to-[#C8587E]/60" data-id="element-141"></span>
              <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-[#C8587E]" aria-hidden="true" data-id="element-142" />
              <span className="h-px w-8 md:w-14 bg-gradient-to-l from-transparent to-[#C8587E]/60" data-id="element-143"></span>
            </div>

            <p className="font-[Cinzel,_serif] italic font-medium text-[#7A2842] text-[16px] md:text-3xl lg:text-4xl tracking-wide max-w-3xl mx-auto" data-id="element-144">
              a partir de hoy.
            </p>
          </div>
          <p className="md:text-2xl text-[#A03B5E] mt-6 mb-8 font-bold font-[Georgia,_serif] text-[18px]" data-id="element-145">
            Planillas listas para usar
          </p>

          {/* Hero Image */}
          <div className="relative z-10 max-w-md md:max-w-2xl mx-auto mb-2" data-id="element-146">
            <div className="relative overflow-hidden rounded-2xl bg-white/40 border border-brand-pink/20 shadow-lg" style={{
            aspectRatio: '1/1',
            maxHeight: '520px'
          }} data-id="element-147">
              <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-contain p-2 md:p-4 pt-[8px] pb-[8px]" draggable={false} data-id="element-148" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 mt-8 space-y-3 px-2" data-id="element-149">
            <p className="md:text-xl font-bold text-brand-rose font-[Georgia,_serif] text-[12px]" data-id="element-150">
              El sistema completo para terapeutas en reflexología que necesitan
              registrar, organizar y hacer seguimiento profesional de sus
              pacientes
            </p>
            <p className="md:text-base text-brand-text/80 font-[Inter,_sans-serif] text-[12px]" data-id="element-151">
              Guía clínica + planillas de registro + control de stock. Todo en
              un solo kit.
            </p>
          </div>

          <div className="flex flex-nowrap items-center justify-center gap-1.5 md:gap-3 mb-12 text-xs md:text-sm font-medium px-2" data-id="element-152">
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-pink/30 whitespace-nowrap shadow-sm text-[9px] md:text-[10px] shrink-0" data-id="element-153">
              <FileText className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-rose shrink-0" data-id="element-154" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-pink/30 whitespace-nowrap shadow-sm text-[9px] md:text-[10px] shrink-0" data-id="element-155">
              <Zap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-rose shrink-0" data-id="element-156" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-pink/30 whitespace-nowrap shadow-sm text-[9px] md:text-[10px] shrink-0" data-id="element-157">
              <span className="mr-1 md:mr-1.5 text-brand-rose shrink-0" data-id="element-158">
                🇦🇷
              </span>
              Hecho para Argentina
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-159">
            <CTAButton className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5" data-id="element-160">
              🦶 → QUIERO MI KIT REFLEXO-PRO AHORA
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-161">
              <div className="flex items-center" data-id="element-162">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5" data-id="element-163" /> Garantía
                7 días
              </div>
              <div className="flex items-center" data-id="element-164">📥 Descarga inmediata</div>
              <div className="flex items-center" data-id="element-165">🖨️ Listo para imprimir</div>
              <div className="flex items-center" data-id="element-166">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5" data-id="element-167" /> Pago
                seguro
              </div>
              <div className="flex items-center" data-id="element-168">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5" data-id="element-169" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-12 border-y border-brand-pink/20 bg-white/50 py-4" data-id="element-170">
        {/* Mobile: wrapped grid */}
        <div className="md:hidden flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm pl-[16px] pr-[16px]" data-id="element-171">
          <div className="flex items-center" data-id="element-172">
            <span className="font-bold text-brand-rose mr-1.5" data-id="element-173">+200</span>
            terapeutas
          </div>
          <div className="flex items-center" data-id="element-174">
            <span className="font-bold text-brand-rose mr-1.5" data-id="element-175">1</span>
            Tomo listo
          </div>
          <div className="flex items-center" data-id="element-176">
            <span className="font-bold text-brand-rose mr-1.5" data-id="element-177">3</span> Bonus
          </div>
          <div className="flex items-center" data-id="element-178">
            <Star className="w-3.5 h-3.5 text-brand-rose fill-brand-rose mr-1" data-id="element-179" />
            4.9/5
          </div>
        </div>

        {/* Desktop: horizontal row with dots */}
        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm" data-id="element-180">
          <div className="flex items-center" data-id="element-181">
            <span className="font-bold text-brand-rose mr-2" data-id="element-182">+200</span>
            terapeutas organizados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-rose/30 shrink-0" data-id="element-183"></div>
          <div className="flex items-center" data-id="element-184">Tomo listo</div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-rose/30 shrink-0" data-id="element-185"></div>
          <div className="flex items-center" data-id="element-186">
            <span className="font-bold text-brand-rose mr-2" data-id="element-187">3</span> Bonus
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-rose/30 shrink-0" data-id="element-188"></div>
          <div className="flex items-center" data-id="element-189">
            <Star className="w-4 h-4 text-brand-rose fill-brand-rose mr-1" data-id="element-190" />
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};